import React, { useCallback, useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import Sidebar from './Sidebar';
import Navigation from './Navigation';
import Dashboard from './Dashboard';
import EMMDashboard from './EMMDashboard';
import CampaignList from './CampaignList';
import CreateCampaign from './CreateCampaign';
import Agents from './Agents';
import EmailTemplates from './EmailTemplates';
import Subscribers from './Subscribers';
import Analytics from './Analytics';
import BlockList from './BlockList';
import BouncedList from './BouncedList';
import RulesEngine from './RulesEngine';
import Clients from './Clients';
import Leads from './Leads';
import Deals from './Deals';
import Meetings from './Meetings';
import Activities from './Activities';
import Contacts from './Contacts';
import TargetAccounts from './TargetAccounts';
import Domains from './Domains';
import EmailAccounts from './EmailAccounts';
import Unibox from './Unibox';
import ExecutiveAssistant from './ExecutiveAssistant';
import AWSAccountCreation from './AWSAccountCreation';
import IdentityHealthMonitoring from './IdentityHealthMonitoring';
import AccountRotationScheduling from './AccountRotationScheduling';
import CrossIdentityCampaignDistribution from './CrossIdentityCampaignDistribution';
import Profile from './Profile';
import UserManagement from './UserManagement';
import Onboarding from './Onboarding';
import SendPoolsAdmin from './SendPoolsAdmin';
import RelayAccountsAdmin from './RelayAccountsAdmin';
import EmailPlatformStatus from './EmailPlatformStatus';
import CompanyProfile from './CompanyProfile';
import CoreApiCanaryBanner from './CoreApiCanaryBanner';
import CompanyTenantSetupGate from './CompanyTenantSetupGate';
import ERMProjects from './erm/ERMProjects';
import ERMProjectDetail from './erm/ERMProjectDetail';
import ERMTemplates from './erm/ERMTemplates';
import ERMArchive from './erm/ERMArchive';
import ERMTasks from './erm/ERMTasks';
import ERMDepartments from './erm/ERMDepartments';
import REExpenses from './re/REExpenses';
import RERevenue from './re/RERevenue';
import REFinancialOverview from './re/REFinancialOverview';
import { isLocalLoopbackHostname } from '../utils/companyTenantResolver';

function shouldShowCoreApiCanaryBanner() {
  if (typeof window === 'undefined' || !window.location) return false;
  const hostname = String(window.location.hostname || '').toLowerCase();
  if (!hostname) return false;
  // Staging is now fixed to core2 by default; keep canary banner limited to localhost debugging.
  return isLocalLoopbackHostname(hostname);
}

const ProtectedRoute = () => {
  const { isAuthenticated, isLoading, error, loginWithRedirect } = useAuth0();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const toggleSidebar = useCallback(() => setSidebarOpen((prev) => !prev), []);
  const closeSidebar = useCallback(() => setSidebarOpen(false), []);

  // Debug logging
  console.log('🔍 ProtectedRoute render:', {
    isLoading,
    isAuthenticated,
    error: error ? error.message : null
  });

  // Handle "Invalid state" error by clearing storage and retrying
  useEffect(() => {
    if (error && error.message === 'Invalid state') {
      console.log('⚠️ Invalid state error detected. Clearing Auth0 storage and retrying...');
      // Clear Auth0 storage
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('@@auth0spajs@@')) {
          localStorage.removeItem(key);
        }
      });
      Object.keys(sessionStorage).forEach(key => {
        if (key.startsWith('@@auth0spajs@@')) {
          sessionStorage.removeItem(key);
        }
      });
      // Retry login after clearing
      setTimeout(() => {
        const redirectUri = typeof window !== 'undefined' && window.location ? window.location.origin : 'http://localhost:3002';
        loginWithRedirect({
          authorizationParams: {
            prompt: 'login',
            redirect_uri: redirectUri
          }
        });
      }, 100);
      return;
    }
  }, [error, loginWithRedirect]);

  // Automatically redirect to Auth0 if not authenticated
  useEffect(() => {
    console.log('🔍 ProtectedRoute useEffect triggered:', {
      isLoading,
      isAuthenticated,
      error: error ? error.message : null,
      shouldRedirect: !isLoading && !isAuthenticated && !error
    });

    if (!isLoading && !isAuthenticated && !error) {
      console.log('🚀 Attempting to redirect to Auth0...');
      const redirectUri = typeof window !== 'undefined' && window.location ? window.location.origin : 'http://localhost:3002';
      loginWithRedirect({
        authorizationParams: {
          prompt: 'login',
          redirect_uri: redirectUri, // Explicitly set to current origin
          scope: 'openid profile email offline_access' // Include offline_access for refresh tokens
        }
      }).catch((err) => {
        console.error('❌ Error during loginWithRedirect:', err);
      });
    }
  }, [isLoading, isAuthenticated, error, loginWithRedirect]);

  if (isLoading || !isAuthenticated) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <div className="mt-3">Redirecting to login...</div>
        </div>
      </div>
    );
  }

  // Show error screen only for non-"Invalid state" errors (we handle "Invalid state" automatically)
  if (error && error.message !== 'Invalid state') {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <div className="text-center">
          <div className="alert alert-danger">
            <h4>Authentication Error</h4>
            <p>{error.message}</p>
            <button 
              className="btn btn-primary" 
              onClick={() => loginWithRedirect({
                authorizationParams: {
                  prompt: 'login'
                }
              })}
            >
              Retry Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <CompanyTenantSetupGate>
      <Sidebar show={sidebarOpen} onClose={closeSidebar} />
      <div className="main-content">
        <Navigation onMenuClick={toggleSidebar} />
        {shouldShowCoreApiCanaryBanner() ? <CoreApiCanaryBanner /> : null}
        <main className="flex-grow-1 p-3">
          <Routes>
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/onboarding" element={<Onboarding />} />
            {/* CRM Routes */}
            <Route path="/clients" element={<Clients />} />
            <Route path="/leads" element={<Leads />} />
            <Route path="/deals" element={<Deals />} />
            <Route path="/meetings" element={<Meetings />} />
            <Route path="/activities" element={<Activities />} />
            <Route path="/contacts" element={<Contacts />} />
            <Route path="/target-accounts" element={<TargetAccounts />} />
            {/* EMM (Email Marketing) Routes */}
            <Route path="/emm-dashboard" element={<EMMDashboard />} />
            <Route path="/agents" element={<Agents />} />
            <Route path="/campaigns" element={<CampaignList />} />
            <Route path="/campaigns/create" element={<CreateCampaign />} />
            <Route path="/templates" element={<EmailTemplates />} />
            <Route path="/subscribers" element={<Subscribers />} />
            <Route path="/analytics" element={<Analytics />} />
            <Route path="/block-list" element={<BlockList />} />
            <Route path="/bounced-list" element={<BouncedList />} />
            <Route path="/rules-engine" element={<RulesEngine />} />
            {/* Identity Management Routes */}
            <Route path="/domains" element={<Domains />} />
            <Route path="/email-accounts" element={<EmailAccounts />} />
            <Route path="/unibox" element={<Unibox />} />
            <Route path="/executive-assistant" element={<ExecutiveAssistant />} />
            <Route path="/aws-accounts" element={<AWSAccountCreation />} />
            <Route path="/aws-management" element={<AWSAccountCreation />} />
            <Route path="/health-monitoring" element={<IdentityHealthMonitoring />} />
            <Route path="/rotation-scheduling" element={<AccountRotationScheduling />} />
            <Route path="/cross-identity" element={<CrossIdentityCampaignDistribution />} />
            {/* Admin Routes */}
            <Route path="/profile" element={<Profile />} />
            <Route path="/users" element={<UserManagement />} />
            <Route path="/admin/company-profile" element={<CompanyProfile />} />
            <Route path="/send-pools" element={<SendPoolsAdmin />} />
            <Route path="/relay-accounts" element={<RelayAccountsAdmin />} />
            <Route path="/admin/email-platform-status" element={<EmailPlatformStatus />} />
            {/* ERM (Employee Relationship Management) Routes */}
            <Route path="/erm/projects" element={<ERMProjects />} />
            <Route path="/erm/projects/:projectId" element={<ERMProjectDetail />} />
            <Route path="/erm/templates" element={<ERMTemplates />} />
            <Route path="/erm/archive" element={<ERMArchive />} />
            <Route path="/erm/tasks" element={<ERMTasks />} />
            <Route path="/erm/departments" element={<ERMDepartments />} />
            {/* R&E (Revenue & Expenses) Routes */}
            <Route path="/re/expenses" element={<REExpenses />} />
            <Route path="/re/revenue" element={<RERevenue />} />
            <Route path="/re/overview" element={<REFinancialOverview />} />
          </Routes>
        </main>
      </div>
    </CompanyTenantSetupGate>
  );
};

export default ProtectedRoute;
